/** Where the home page's natural-language search box opens (CLE-550).
 *
 * The box sends the question to the agent and takes the first people or
 * company search the agent interpreted. The API's server-validated criteria
 * are loaded as they are (clu-criteria-handoff); only a result without them
 * (an older one) goes through the legacy agent mapper. Criteria the page
 * cannot take open nothing, so the caller falls back to a chat turn rather
 * than searching with a value left out.
 */
import type { CopilotSearchResult } from '@/hooks/use-copilot-search.hook';
import { INITIAL_FILTERS } from '@/types/search-filters';
import {
  crmExclusionsOnlyTheChatApplies,
  mapAgentCompanySearchParamsToCompanyFilters,
  mapAgentSearchParamsToCompanyFilters,
  mapAgentSearchParamsToFilters,
} from '@/utils/agent-filters-to-search-filters';
import { INITIAL_COMPANY_FILTERS } from '@/utils/company-search.utils';
import { encodeSearchQuery } from '@/utils/search-query-encoding';
import { cluCompanyHandoff, cluPeopleHandoff } from './clu-criteria-handoff';

export interface LandingSearchHandoff {
  /** The finder's `tab` search param. */
  tab: 'people' | 'companies';
  /** The finder's `q` search param. */
  q: string;
}

function warnNotOpened(error: string) {
  console.warn('[clu-criteria] landing search not opened:', error);
}

export function landingSearchHandoff(
  result: CopilotSearchResult
): LandingSearchHandoff | null {
  if (result.kind === 'company') {
    const handoff = cluCompanyHandoff(result.criteria);
    if (handoff.kind === 'ready') return { tab: 'companies', q: handoff.q };
    if (handoff.kind === 'invalid') warnNotOpened(handoff.error);
    if (handoff.kind !== 'absent') return null;
    // company_search result -> Companies tab (CLE-401).
    const mappedCo = mapAgentCompanySearchParamsToCompanyFilters(
      result.searchParams
    );
    if (Object.keys(mappedCo).length === 0) return null;
    return {
      tab: 'companies',
      q: encodeSearchQuery({
        tab: 'company',
        filters: { ...INITIAL_COMPANY_FILTERS, ...mappedCo },
      }),
    };
  }

  const handoff = cluPeopleHandoff(result.criteria, result.companyCriteria);
  if (handoff.kind === 'ready') return { tab: 'people', q: handoff.q };
  if (handoff.kind === 'invalid') warnNotOpened(handoff.error);
  if (handoff.kind !== 'absent') return null;
  // An exclusion only the chat applies (any Attio clause, "not being
  // outreached") would be dropped by the page, so the chat answers instead.
  const chatOnly = crmExclusionsOnlyTheChatApplies(result.searchParams);
  if (chatOnly.length > 0) {
    warnNotOpened(`exclusions the page can't apply: ${chatOnly.join(', ')}`);
    return null;
  }
  const mapped = mapAgentSearchParamsToFilters(result.searchParams);
  const mappedCompany = mapAgentSearchParamsToCompanyFilters(
    result.searchParams
  );
  if (
    Object.keys(mapped).length === 0 &&
    Object.keys(mappedCompany).length === 0
  )
    return null;
  const filters: Record<string, unknown> = { ...INITIAL_FILTERS, ...mapped };
  // Composite firmographics (funding, revenue, …) ride under the namespaced
  // key the /search page extracts — same shape as the in-chat handoff
  // (PeopleSearchHandoffSurface).
  if (Object.keys(mappedCompany).length > 0) {
    filters.__compositeCompanyFilters = mappedCompany;
  }
  return { tab: 'people', q: encodeSearchQuery({ tab: 'people', filters }) };
}
